"use client";

import { FC, HTMLAttributes } from "react";
import Link from "next/link";
import Image from "next/image";

import logo from "@/assets/images/branding/logo-white.svg";
import instagramIcon from "@/assets/images/footer/instagram.svg";
import twitterIcon from "@/assets/images/footer/twitter.svg";
import linkedinIcon from "@/assets/images/footer/linkedin.svg";
import dsci from "@/assets/images/footer/dsci.png";
import iso from "@/assets/images/footer/iso-27001.png";
import certIn from "@/assets/images/footer/cert-in.png";
// Self-hosted rather than hot-linked from abdm.gov.in/strapicms, so the badge
// can't break if the government CMS moves or renames the asset.
import abdm from "@/assets/images/footer/abdm.svg";
import facebookIcon from "@/assets/images/footer/facebook.svg";

import { useQuery } from "@tanstack/react-query";

import useNavigation from "@/hooks/useNavigation";
import { socialMediaLinks } from "@/content/socialMedia";
import { IHeaderMenuItem } from "../header/DesktopHeader";
import { getCurrentYear } from "@/utils/format-date.helper";
import NewsLetterSubscribeSection from "@/sections/blogs/NewsLetterSubscribeSection";
import { companyInfoQuery } from "@/api/company-info";
import {
  COMPANY_INFO_FALLBACK,
  officeRegionLine,
  telHref,
} from "@/content/companyInfo";

// ------------------------------------------------------------------------------------

const currentYear = getCurrentYear();

// ------------------------------------------------------------------------------------

export type IFooterProps = HTMLAttributes<HTMLElement> & {};

const Footer: FC<IFooterProps> = () => {
  const { services, industries, navigation } = useNavigation();

  // Address/phone/email are CMS-managed (Strapi "Company Info"). initialData is
  // the canonical fallback so the block renders instantly and never blanks if
  // the fetch is pending or fails.
  const { data: companyInfo } = useQuery({
    ...companyInfoQuery.get(),
    initialData: COMPANY_INFO_FALLBACK,
  });
  const offices = companyInfo?.offices?.length
    ? companyInfo.offices
    : COMPANY_INFO_FALLBACK.offices;
  const phone = companyInfo?.phone || COMPANY_INFO_FALLBACK.phone;
  const email = companyInfo?.email || COMPANY_INFO_FALLBACK.email;

  return (
    <footer className="w-full bg-black-950">
      {/* Newsletter strip — lives in the footer so every page surfaces a
          subscribe opportunity (and the home page's Strapi-driven
          newsletter_section can be removed without losing the lead-gen
          surface). The NewsLetterSubscribeSection already renders inside
          its own <section> with container padding, so we just drop it in
          and it inherits the dark footer bg behind its gradient card. */}
      <NewsLetterSubscribeSection className="!container !mx-auto !pt-12 md:!pt-16 lg:!pt-20" />

      <div className="container">
        <div className="flex h-auto flex-col justify-between">
          <div className="justify-between pb-10 pt-8 md:pb-12 md:pt-10 lg:flex lg:justify-normal lg:pb-16 lg:pt-12">
            <div>
              <div className="md:mr-16 2xl:mr-20">
                <Link
                  href="/"
                  aria-label="Certbar Security home"
                  className="flex items-center"
                  prefetch={true}
                >
                  <Image
                    src={logo.src}
                    alt="Certbar Security"
                    height={46}
                    width={197}
                    style={{
                      objectFit: "contain",
                      height: "auto",
                      width: "auto",
                    }}
                  />
                </Link>
                <p className="mb-6 mt-4 text-sm font-normal tracking-wide text-black-200">
                  Attack. Defend. Comply. Privacy.
                </p>
                <div className="mb-8 flex items-center gap-6">
                  <Link
                    href={socialMediaLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <Image
                      src={instagramIcon.src}
                      alt="Instagram"
                      height={25}
                      width={25}
                      style={{
                        objectFit: "contain",
                        height: "auto",
                        width: "auto",
                      }}
                    />
                  </Link>

                  <Link
                    href={socialMediaLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                  >
                    <Image
                      src={twitterIcon.src}
                      alt="Twitter"
                      height={25}
                      width={25}
                      style={{
                        objectFit: "contain",
                        height: "auto",
                        width: "auto",
                      }}
                    />
                  </Link>

                  <Link
                    href={socialMediaLinks.linkdin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                  >
                    <Image
                      src={linkedinIcon.src}
                      alt="Linkedin"
                      height={25}
                      width={25}
                      style={{
                        objectFit: "contain",
                        height: "auto",
                        width: "auto",
                      }}
                    />
                  </Link>

                  <Link
                    href={socialMediaLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <Image
                      src={facebookIcon.src}
                      alt="Facebook"
                      height={25}
                      width={25}
                      style={{
                        objectFit: "contain",
                        height: "auto",
                        width: "auto",
                      }}
                    />
                  </Link>
                </div>

                {/* Contact info — CMS-managed via Strapi "Company Info"
                    (offices/phone/email). The CERT-In/ISO/DSCI badges that
                    PR #698 added inline here have been relocated to the
                    full-width "Register with" trust strip below. */}
                <address className="not-italic">
                  <div className="flex flex-col gap-5">
                    {offices.map((office) => (
                      <div key={office.label}>
                        <p className="text-xs font-medium uppercase tracking-wider text-white">
                          {office.label}
                        </p>
                        <p className="mt-2 text-sm font-normal leading-relaxed text-black-200">
                          {office.streetAddress}
                          <br />
                          {officeRegionLine(office)}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex flex-col gap-2">
                    <a
                      href={telHref(phone)}
                      className="text-sm font-normal text-black-200 transition-colors hover:text-white"
                    >
                      {phone}
                    </a>
                    <a
                      href={`mailto:${email}`}
                      className="text-sm font-normal text-black-200 transition-colors hover:text-white"
                    >
                      {email}
                    </a>
                  </div>
                </address>
              </div>
            </div>

            {/* Menu columns — grid keeps every column the same width.
                Mobile = 2 cols, tablet = 3 cols, desktop = 5 cols, so 5
                evenly-distributed columns sit alongside the brand block
                without one stretching wider than another. */}
            <div className="mb-8 mt-10 grid flex-1 grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 md:mt-0 md:grid-cols-5 md:gap-y-0">
              {/* Services menu */}
              {services && (
                <div>
                  <h2>
                    <span className="font-bricolage-grotesque text-xs font-medium uppercase tracking-wider text-white">
                      {services.label}
                    </span>
                  </h2>

                  <ul className="mt-4 flex flex-col gap-2 md:gap-3 lg:mt-6">
                    {services.items.map((item, index) => (
                      <li key={index}>
                        {item.categoryLink ? (
                          <Link
                            href={item.categoryLink}
                            className="text-sm font-normal text-black-200 transition-colors hover:text-white"
                          >
                            {item.heading}
                          </Link>
                        ) : (
                          <span className="text-sm font-normal text-black-200 transition-colors hover:text-white">
                            {item.heading}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {([industries, ...navigation] as IHeaderMenuItem[]).map(
                (item, idx) =>
                  item && (
                    <div key={idx}>
                      <h2>
                        <span className="font-bricolage-grotesque text-xs font-medium uppercase tracking-wider text-white">
                          {item.label}
                        </span>
                      </h2>

                      <ul className="mt-4 flex flex-col gap-2 md:gap-3 lg:mt-6">
                        {item.items?.slice(0, 8).map(
                          (itm, index) =>
                            itm &&
                            (itm.footer === undefined || itm.footer) && (
                              <li key={index}>
                                {itm.href ? (
                                  <Link
                                    href={itm.href}
                                    className="text-sm font-normal text-black-200 transition-colors hover:text-white"
                                  >
                                    {itm.label}
                                  </Link>
                                ) : (
                                  <span className="text-sm font-normal text-black-200 transition-colors hover:text-white">
                                    {itm.label}
                                  </span>
                                )}
                              </li>
                            ),
                        )}
                      </ul>
                    </div>
                  ),
              )}
            </div>
          </div>
          {/* Full-width trust strip — pulled out of the brand column so the
              three empanelment / certification badges read as one horizontal
              row. Uniform h-10 md:h-12 normalizes the visual size of badges
              that have very different source aspect ratios (CERT-In is wide,
              ISO 27001 is tall/square, DSCI is short/wide). */}
          <div className="flex flex-col items-start gap-3 border-t-[0.5px] border-black-400 pt-6 md:flex-row md:items-center md:gap-6 md:pt-7">
            <span className="font-bricolage-grotesque text-xs font-medium uppercase tracking-wider text-white md:text-sm">
              Register with
            </span>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 md:gap-x-8">
              <Image
                src={certIn.src}
                alt="CERT-In Empanelled"
                height={48}
                width={213}
                className="h-10 w-auto md:h-12"
                style={{ objectFit: "contain" }}
              />
              <Image
                src={iso.src}
                alt="ISO 27001 Certified"
                height={48}
                width={48}
                className="h-10 w-auto md:h-12"
                style={{ objectFit: "contain" }}
              />
              <Image
                src={dsci.src}
                alt="DSCI"
                height={48}
                width={125}
                className="h-10 w-auto md:h-12"
                style={{ objectFit: "contain" }}
              />
              {/* Source SVG is 363x335, so width is set to match that ratio at
                  h-48; the shared h-10/md:h-12 class then normalises it against
                  the other badges. */}
              <Image
                src={abdm.src}
                alt="Ayushman Bharat Digital Mission (ABDM)"
                height={48}
                width={52}
                className="h-10 w-auto md:h-12"
                style={{ objectFit: "contain" }}
              />
            </div>
          </div>
          <div className="mb-6 mt-8 items-baseline justify-between border-t-[0.5px] border-black-400 pt-6 md:text-center lg:flex lg:text-left">
            <span className="text-xs font-normal text-black-300 md:text-sm">
              © 2019 — {currentYear} Certbar Security Private Limited. All rights reserved.
            </span>
            <ul className="mt-3 flex flex-wrap gap-x-2 gap-y-2 md:justify-center lg:mt-0 lg:justify-end lg:gap-x-5 xl:gap-x-7">
              <li className="mb-2 w-1/2 text-left md:mb-0 md:w-auto">
                <Link href="/privacy-policy" className="text-sm font-normal text-black-200 transition-colors hover:text-white">
                  Privacy Policy
                </Link>
              </li>
              <li className="mb-2 w-1/2 text-left md:mb-0 md:w-auto">
                <Link href="/terms-of-use" className="text-sm font-normal text-black-200 transition-colors hover:text-white">
                  Terms of Use
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
